/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React from "react";

import { getSalesDashboardDateRangeValidationError } from "./salesDashboardDateRangeValidation";

export type SalesDashboardDateRangeInputValue = {
  startDateTime: string;
  endDateTime: string;
};

const validationErrorMessages: Record<
  NonNullable<ReturnType<typeof getSalesDashboardDateRangeValidationError>>,
  string
> = {
  dateTimeRangeRequired: "開始日時と終了日時を入力してください",
  startDateTimeRequired: "開始日時を入力してください",
  endDateTimeRequired: "終了日時を入力してください",
  startDateTimeAfterEndDateTime: "開始日時は終了日時以前に設定してください",
};

export const SalesDashboardDateRangeInputs: React.FC<{
  disabled: boolean;
  value: SalesDashboardDateRangeInputValue;
  onChange: (value: SalesDashboardDateRangeInputValue) => void;
}> = ({ disabled, value, onChange }) => {
  const validationErrorMessageId = React.useId();
  const [hasInteractedWithInputs, setHasInteractedWithInputs] =
    React.useState(false);
  const validationError = hasInteractedWithInputs
    ? getSalesDashboardDateRangeValidationError(value)
    : undefined;
  const validationErrorMessage = validationError
    ? validationErrorMessages[validationError]
    : undefined;
  const isStartDateTimeRequired =
    validationError === "dateTimeRangeRequired" ||
    validationError === "startDateTimeRequired";
  const isEndDateTimeRequired =
    validationError === "dateTimeRangeRequired" ||
    validationError === "endDateTimeRequired";
  const isDateTimeRangeReversed =
    validationError === "startDateTimeAfterEndDateTime";
  const isStartDateTimeInvalid =
    isStartDateTimeRequired || isDateTimeRangeReversed;
  const isEndDateTimeInvalid = isEndDateTimeRequired || isDateTimeRangeReversed;

  return (
    <div css={styles.container}>
      <div
        css={[
          styles.control,
          validationError ? styles.controlError : null,
          disabled ? styles.controlDisabled : null,
        ]}
        role="group"
        aria-label="集計期間"
        aria-describedby={
          validationErrorMessage ? validationErrorMessageId : undefined
        }
      >
        <span css={styles.label}>集計期間</span>
        <div css={styles.rangeFields}>
          <input
            css={[
              styles.dateTimeInput,
              isStartDateTimeRequired ? styles.requiredDateTimeInput : null,
            ]}
            type="datetime-local"
            name="sales-dashboard-start-date-time"
            aria-label="集計開始日時"
            aria-invalid={isStartDateTimeInvalid || undefined}
            disabled={disabled}
            step={60}
            max={value.endDateTime || undefined}
            value={value.startDateTime}
            onBlur={() => {
              setHasInteractedWithInputs(true);
            }}
            onChange={(event) => {
              setHasInteractedWithInputs(true);
              onChange({
                ...value,
                startDateTime: event.currentTarget.value,
              });
            }}
          />
          <span css={styles.separator} aria-hidden="true">
            〜
          </span>
          <input
            css={[
              styles.dateTimeInput,
              isEndDateTimeRequired ? styles.requiredDateTimeInput : null,
            ]}
            type="datetime-local"
            name="sales-dashboard-end-date-time"
            aria-label="集計終了日時"
            aria-invalid={isEndDateTimeInvalid || undefined}
            disabled={disabled}
            step={60}
            min={value.startDateTime || undefined}
            value={value.endDateTime}
            onBlur={() => {
              setHasInteractedWithInputs(true);
            }}
            onChange={(event) => {
              setHasInteractedWithInputs(true);
              onChange({
                ...value,
                endDateTime: event.currentTarget.value,
              });
            }}
          />
        </div>
      </div>
      {validationErrorMessage ? (
        <p
          css={styles.validationErrorMessage}
          id={validationErrorMessageId}
          role="alert"
        >
          {validationErrorMessage}
        </p>
      ) : null}
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: 4px;
  `,
  control: css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 38px;
    padding: 9px 14px;
    background: #22313f;
    border: 1px solid #2e3d4c;
    border-radius: 8px;
  `,
  controlError: css`
    border-color: #ff6b6b;
  `,
  controlDisabled: css`
    opacity: 0.6;
    cursor: not-allowed;
  `,
  label: css`
    flex: 0 0 auto;
    color: #8b96a2;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
    white-space: nowrap;
  `,
  rangeFields: css`
    display: grid;
    flex: 1 1 auto;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 8px;
    min-width: 0;
  `,
  dateTimeInput: css`
    width: 100%;
    min-width: 0;
    padding: 0px 4px;
    font-family: inherit;
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.3px;
    background: transparent;
    border: none;
    cursor: pointer;
    color-scheme: dark;
    &:focus-visible {
      border-radius: 2px;
      outline: 2px solid #00c2ae;
      outline-offset: 2px;
    }
    &:disabled {
      cursor: not-allowed;
    }
  `,
  requiredDateTimeInput: css`
    background: rgba(255, 0, 0, 0.1);
    border-radius: 3px;
    box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.1);
  `,
  separator: css`
    color: #8b96a2;
    font-size: 12px;
  `,
  validationErrorMessage: css`
    margin: 0;
    color: #ff6b6b;
    font-size: 14px;
    line-height: 1.4;
  `,
};
